/* ⚠️ GENERATED from app/services/liveRules.ts by gen-twins.mjs — edit the TypeScript, not this. */
/* ═══════════════════════════════════════════════════════════════════════════
   📍 services/liveRules.ts — the pure rules behind live location.

   1. IS THIS ROW A LOCATION?
      🟥 A live location that reached a phone on a weak connection was drawn first
         as a plain text bubble — "📍 Live Location" — and only became a map when
         the Firestore copy arrived. Every route that builds a row from a push
         (socket bridge, foreground ingest, background handler) decided the type
         from `type`/`msgType` and `latitude`, and a location has NO plaintext
         latitude: its coordinates live only in the sealed `locationPerDeviceKeys`.
         So a push whose type was lost, or a row cached by an older build, was
         typed 'text' and drew its notification label.
      🟢 The sealed fields (and the live-session id) ARE the type. And a text row
         that carries nothing encrypted and reads like the location label is that
         label — every real text message on this app is end-to-end encrypted, so
         an unencrypted "📍 …" from someone else is a placeholder, not words.

   2. WHEN DOES A SHARER SEND A NEW FIX?
      Moved far enough, or long enough since the last one (so "updated just now"
      stays true while standing still), but never in a burst.

   3. HOW OLD IS WHAT I AM LOOKING AT? ("Updated 3 min ago")
   ═══════════════════════════════════════════════════════════════════════════ */

/* ═══ 1. IS THIS ROW A LOCATION? ══════════════════════════════════════════ */
const LOC_FIELDS = ['locationPerDeviceKeys', 'groupLocationEncryptionData', 'encryptedLocationPayload', 'liveLocationId'];

/** A push / row payload that is a location, whatever its type says. */
export function looksLikeLocation(d) {
  if (!d || typeof d !== 'object') return false;
  if (d.type === 'location' || d.msgType === 'location') return true;
  if (d.latitude != null && d.latitude !== '' && d.longitude != null && d.longitude !== '') return true;
  return LOC_FIELDS.some(k => d[k] != null && d[k] !== '' && d[k] !== 'null');
}
const isEncryptedText = m => !!(m && (m.nonce || m.perDeviceKeys || m.cipherText || m.groupEncryptionData || m.encrypted === true));

/** A text row that is only the location's notification label. */
export function isLocationLabelRow(m, meId) {
  if (!m || m.type && m.type !== 'text') return false;
  if (m.isAI || m.isDeleted || m.systemType) return false;
  if (meId && String(m.senderId || '') === String(meId)) return false;
  if (isEncryptedText(m)) return false;
  const s = typeof m.text === 'string' ? m.text.trim() : '';
  return s.startsWith('📍') && s.length <= 48 && !s.includes('\n');
}

/** The row as it should be drawn: a location stays a location. Same object when nothing changes. */
export function asLocationRow(m, meId) {
  if (!m || m.type === 'location') return m;
  if (m.type && m.type !== 'text') return m;
  if (!(looksLikeLocation(m) || isLocationLabelRow(m, meId))) return m;
  return {
    ...m,
    type: 'location',
    live: m.live === true || m.live === 'true' || !!m.liveLocationId && m.live !== false
  };
}

/* ═══ 2. WHEN DOES A SHARER SEND A NEW FIX? ═══════════════════════════════ */
/** Moved at least this far → send now. */
export const LIVE_MIN_MOVE_M = 15;
/** Standing still → still send this often, so the other side sees "just now". */
export const LIVE_HEARTBEAT_MS = 45_000;
/** Never two fixes closer together than this. */
export const LIVE_MIN_GAP_MS = 5_000;
/** Older than this, the viewer is told the position may be stale. */
export const LIVE_STALE_MS = 3 * 60_000;
export function distanceM(a, b) {
  const R = 6371000;
  const toR = x => x * Math.PI / 180;
  const dLat = toR(b.lat - a.lat);
  const dLng = toR(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a.lat)) * Math.cos(toR(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
export function validFix(f) {
  return !!f && Number.isFinite(f.lat) && Number.isFinite(f.lng) && Math.abs(f.lat) <= 90 && Math.abs(f.lng) <= 180 && !(f.lat === 0 && f.lng === 0);
}

/** Should this new position go out? `force` = the person asked (the refresh button). */
export function shouldSendFix(last, next, now, force = false) {
  if (!validFix(next)) return false;
  if (!last) return true;
  const gap = now - last.at;
  if (gap < LIVE_MIN_GAP_MS) return false;
  if (force) return true;
  if (gap >= LIVE_HEARTBEAT_MS) return true;
  return distanceM(last, next) >= LIVE_MIN_MOVE_M;
}

/** A session past its end, by its own clock. */
export function liveExpired(untilMs, now) {
  const u = Number(untilMs);
  return Number.isFinite(u) && u > 0 && now >= u;
}

/* ═══ 2b. LIVE, ENDED, OR AN ORDINARY LOCATION? ═══════════════════════════
   🟥 A share that RAN OUT showed the blue "LOCATION" pill — the card for a pin
      somebody dropped once — because only `liveEnded` (written by the Stop
      button) said "ended", and nobody presses Stop on a share that runs out.
      And on entering the chat it flickered: the saved row said ENDED, then a
      fresher copy of the message (live:true, no liveEnded) landed on top and
      the pill flipped to LOCATION.
   🟢 One answer from every field that can say it, and it only moves forward:
      a row that was ever a live share is 'live' while live and unexpired, and
      'ended' otherwise — never 'static'. 'static' is only a location that was
      never live. */

export function liveStateOf(m, now) {
  if (!m) return 'static';
  const liveFlag = m.live === true || m.live === 'true';
  const wasLive = liveFlag || !!m.liveLocationId || m.liveEnded === true || !!m.liveUntil;
  if (!wasLive) return 'static';
  if (m.liveEnded === true || !liveFlag) return 'ended';
  if (liveExpired(toMillis(m.liveUntil), now)) return 'ended';
  return 'live';
}

/* ═══ 2c. IS A SESSION DOCUMENT STILL SHARING? ═════════════════════════════
   🟥 "I have an active live location from 20 days ago — still live." The live-locations list
      believed `isActive`, and `isActive: false` is written only by a phone that ENDS the share: the
      Stop button, or the sharing phone's own timer. A share whose phone never ran it out (the app
      reinstalled, signed out, another phone, an older build that kept no session on disk) stays
      `isActive: true` for ever — while the chat bubble, which also reads `liveUntil`, said ended.
   🟢 One answer for every reader: a session is sharing while it is not stopped AND not past its end.
   🟥 …AND IT HAS AN END. "I updated, opened Privacy → Live location and saw three live locations I never
      sent." Older builds ran a second update timer that wrote each fix to a document of its own — chat,
      sender, sealed position, time; no message, no end, no flag — and only cleaned it up if the app was
      still running when the share ended. The first version of this rule read "no end at all" as "the
      flag is all there is", and with no flag either, those leftovers were "Sharing now", with a Stop.
      Every real share has carried its end time from its first write (the longest is 8 hours): a
      document without one is not a share, and it is never live. */
export function sessionHasEnd(d) {
  return !!d && toMillis(d.liveUntil) > 0;
}
export function sessionLive(d, now) {
  if (!d || d.isActive === false || !sessionHasEnd(d)) return false;
  return !liveExpired(toMillis(d.liveUntil), now);
}
/** a document still not marked ended although it is not sharing (ran out, or never had an end) — the
 *  sharer closes it quietly */
export function sessionRanOut(d, now) {
  return !!d && d.isActive !== false && (!sessionHasEnd(d) || liveExpired(toMillis(d.liveUntil), now));
}

/* ═══ 3. HOW OLD IS IT? ═══════════════════════════════════════════════════ */
export function toMillis(v) {
  if (!v) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toMillis === 'function') {
    try {
      return v.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
  if (v instanceof Date) return v.getTime();
  const p = Date.parse(String(v));
  return Number.isFinite(p) ? p : 0;
}

/** { key, n, stale } for "Updated just now / 3 min ago / 2 h ago"; null when unknown. */
export function liveAge(updatedAt, now) {
  const at = toMillis(updatedAt);
  if (!at) return null;
  const ms = Math.max(0, now - at);
  const stale = ms >= LIVE_STALE_MS;
  if (ms < 60_000) return {
    key: 'live_updated_now',
    n: 0,
    stale
  };
  const min = Math.floor(ms / 60_000);
  if (min < 60) return {
    key: 'live_updated_min',
    n: min,
    stale
  };
  return {
    key: 'live_updated_hr',
    n: Math.floor(min / 60),
    stale
  };
}

/* ⏳ r138 — "until 3:45 PM" / "2 h left" for a live share's pill (the bubble and the location screen). Pure. */
export function liveUntilLabel(untilMs, t, now = Date.now()) {
  const left = untilMs - now;
  if (!Number.isFinite(untilMs) || left <= 0) return '';
  if (left < 60 * 60 * 1000) {
    const m = Math.max(1, Math.round(left / 60000));
    return t('loc_live_minutes_left', {
      count: m
    }) || `${m} min left`;
  }
  const clock = new Date(untilMs).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
  return t('loc_live_until', {
    time: clock
  }) || `until ${clock}`;
}
